<script setup lang="ts">
import { computed } from 'vue';

import IncomingPropertyReceiveRentalIncome from '@/components/application/incoming-property-receive-rental-income.vue';
import IncomingPropertyExpectedRentalIncome from '@/components/application/incoming-property-expected-rental-income.vue';
import IncomingPropertyTransferFeeCovered from '@/components/application/incoming-property-transfer-fee-covered.vue';
import IncomingPropertyIsNewBuild from '@/components/application/incoming-property-is-new-build.vue';
import IncomingPropertyContractExchangedBeforeMay2026 from '@/components/application/incoming-property-contract-exchanged-before-may-2026.vue';
import { stampDutyStatusOptions, PropertyPurpose } from '@/helpers/const';
import { useNegativeGearingFy27Asset } from '@/helpers/useNegativeGearingFy27Asset';

import type { DepositPaidType } from '@/api/enums';
import type { PropertyPurposeType } from '@/types/api';

const props = defineProps<{
  id: string;
  contractForSale?: boolean;
  receiveRentalIncome?: boolean;
  propertyPurpose?: PropertyPurposeType;
  isRetirementPropertyType?: boolean; // add this
  isNewBuild?: boolean;
  erhlSavingsAmount?: number;
}>();

const depositTypeOptions: Record<DepositPaidType, string> = {
  ['cash_payment']: 'Cash payment',
  ['deposit_bond']: 'Deposit bond',
};

const showRentalIncomeQuestion = computed(() => props.propertyPurpose === PropertyPurpose.INVESTMENT_PROPERTY);

const { showNewBuild, showContractExchange } = useNegativeGearingFy27Asset(
  computed(() => props.propertyPurpose),
  computed(() => props.isNewBuild),
);

const showContractExchangeQuestion = computed(() => showContractExchange.value && props.contractForSale === true);
</script>

<template>
  <div class="mt-6">
    <IncomingPropertyReceiveRentalIncome
      v-if="showRentalIncomeQuestion && !isRetirementPropertyType"
      :id="id"
      label="Does the applicant expect to receive rental income for this property?"
    />
    <IncomingPropertyExpectedRentalIncome v-if="receiveRentalIncome" :id="id" />
    <BxField v-slot="{ field }" name="contractForSale">
      <BxRadio
        v-bind="field"
        :id="`${id}-contract-of-sale`"
        :data-test-id="`${id}-contract-of-sale`"
        label="Is there a contract for sale?"
        label-type="default"
        class="form-control"
        boolean
      />
    </BxField>
    <IncomingPropertyIsNewBuild v-if="showNewBuild" :id="id" />
    <IncomingPropertyContractExchangedBeforeMay2026 v-if="showContractExchangeQuestion" :id="id" />
    <BxField v-if="contractForSale !== undefined" v-slot="{ field }" name="settlementDate">
      <BxDatePicker
        v-bind="field"
        :id="`${id}-${contractForSale ? 'settlement-date' : 'estimated-purchase-date'}`"
        :data-test-id="`${id}-settlement-date-estimated-purchase-date`"
        :label="contractForSale ? 'Settlement date' : 'Estimated settlement date'"
        label-type="floating"
        placeholder="DD/MM/YYYY"
        class="form-control"
        :min-date="new Date()"
      />
    </BxField>
    <BxField v-if="contractForSale !== undefined" v-slot="{ field }" name="purchaseAmount">
      <BxNumberInput
        v-bind="field"
        :id="`${id}-${contractForSale ? 'purchase-price' : 'estimated-purchase-price'}`"
        :data-test-id="`${id}-purchase-price-estimated-purchase-price`"
        :label="contractForSale ? 'Purchase price' : 'Estimated purchase price'"
        :max="999999999"
        label-type="floating"
        leading-symbol="$"
        class="form-control"
      />
    </BxField>
    <BxField v-if="contractForSale !== undefined" v-slot="{ field }" name="depositAmount">
      <BxNumberInput
        v-bind="field"
        :id="`${id}-${contractForSale ? 'deposit-amount' : 'estimated-deposit-amount'}`"
        :data-test-id="`${id}-deposit-amount-estimated-deposit-amount`"
        :label="contractForSale ? 'Deposit amount' : 'Estimated deposit amount'"
        :max="999999999"
        label-type="floating"
        leading-symbol="$"
        class="form-control"
      />
    </BxField>
    <BxField v-if="contractForSale !== undefined" v-slot="{ field }" name="depositType">
      <BxRadio
        v-bind="field"
        :id="`${id}-deposit-type`"
        :data-test-id="`${id}-deposit-type`"
        :options="depositTypeOptions"
        :label="contractForSale ? 'How was the deposit paid?' : 'How is the deposit being paid?'"
        label-type="default"
        class="form-control"
      />
    </BxField>
    <BxField
      v-if="contractForSale !== undefined && !isRetirementPropertyType"
      v-slot="{ field }"
      name="stampDutyStatus"
    >
      <BxRadio
        v-bind="field"
        :id="`${id}-stamp-duty-paid`"
        :data-test-id="`${id}-stamp-duty-paid`"
        :options="stampDutyStatusOptions"
        label="How will stamp duty be paid?"
        label-type="default"
        block
        class="form-control"
      />
    </BxField>
    <IncomingPropertyTransferFeeCovered
      v-if="contractForSale !== undefined"
      :id="id"
      label="Does the applicant need the transfer fee included in the loan amount?"
    />
    <BxField v-slot="{ field }" name="erhlSavingsAmount" class="mb-2">
      <BxNumberInput
        v-bind="field"
        label="Additional contributions amount"
        label-type="floating"
        leading-symbol="$"
        :allow-negative="false"
        class="form-control"
        help-text="Leave blank if there are no additional contributions."
      />
    </BxField>
    <p class="mb-6 text-sm text-gray-500">Do not include the deposit to be paid on the new property</p>

    <BxField v-if="erhlSavingsAmount" v-slot="{ field }" name="erhlGiftedAmount" class="mb-6">
      <BxNumberInput
        v-bind="field"
        label="How much of these funds have been gifted?"
        label-type="floating"
        leading-symbol="$"
        :allow-negative="false"
        class="form-control"
      />
    </BxField>
  </div>
</template>
